import { CursorPaginatedResponseSchema, OneOrManySchema, QueryBooleanSchema } from 'notable-common';
import { z } from 'zod';
import { NotificationIntentSchema } from './intent.js';
import { Language } from '../demographics/index.js';
/** The channel a notification content is written for. Its localizations are rendered for this one. */
export var NotificationContentModality;
(function (NotificationContentModality) {
    NotificationContentModality["Email"] = "EMAIL";
    NotificationContentModality["Sms"] = "SMS";
    NotificationContentModality["Voice"] = "VOICE";
})(NotificationContentModality || (NotificationContentModality = {}));
/**
 * Where a notification content came from. `findAll` defaults to `Manual`, so a content created by
 * a broadcast or generated by the product is invisible to the staff list unless asked for.
 */
export var NotificationContentSource;
(function (NotificationContentSource) {
    NotificationContentSource["Manual"] = "MANUAL";
    NotificationContentSource["Generated"] = "GENERATED";
    NotificationContentSource["Broadcast"] = "BROADCAST";
})(NotificationContentSource || (NotificationContentSource = {}));
/** One language's rendering of a content. `subject` is only meaningful for email. */
export const NotificationContentLocalizationSchema = z.object({
    content: z.string(),
    subject: z.string().nullable(),
});
export const NotificationContentLocalizationMapSchema = z.partialRecord(z.enum(Language), NotificationContentLocalizationSchema);
/**
 * A message template: one body per language, plus the reply intents that branch a conversation on
 * from it. Nothing here says who receives it or when — a notification schedule pairs a content with
 * an audience and a trigger.
 */
export const NotificationContentSchema = z.object({
    id: z.uuid(),
    created: z.iso.datetime(),
    updated: z.iso.datetime(),
    name: z.string(),
    organizationId: z.uuid().nullable(),
    kind: z.enum(NotificationContentModality),
    localizations: NotificationContentLocalizationMapSchema,
    intents: z.array(NotificationIntentSchema),
    deletedTimestamp: z.iso.datetime().nullable(),
    source: z.enum(NotificationContentSource).nullable().optional(),
    // Only the list query builds this — it prefixes the organization name to disambiguate contents
    // that several organizations have named the same thing.
    uniqueName: z.string().nullable().optional(),
});
/**
 * Path params for the routes addressing one content: get, clone, update and remove.
 *
 * Not strict, matching what the endpoints validate today. A path parameter cannot carry an unknown
 * key anyway — Express fills it from the route pattern.
 */
export const NotificationContentParamsSchema = z.object({
    contentId: z.uuid(),
});
/**
 * A localization on the way in, where `subject` may be omitted or sent as null. At least one
 * language is required — a content with no body is not sendable.
 */
export const NotificationContentUpsertLocalizationMapSchema = z
    .partialRecord(z.enum(Language), z.object({
    content: z.string(),
    subject: z.string().nullable().optional(),
}))
    .refine((map) => Object.keys(map).length > 0, {
    message: 'At least one localization is required',
});
/**
 * An intent on the way in. `id` identifies an existing row to update; without one the intent is
 * created, and an existing intent left out of the array is deleted.
 */
export const NotificationContentUpsertIntentSchema = z.object({
    id: z.uuid().optional(),
    intent: z.string(),
    replies: z.array(z.string()),
    nextNotificationContentId: z.string().nullable().optional(),
});
export const NotificationContentCreateSchema = z.object({
    name: z.string(),
    organizationId: z.uuid().optional(),
    kind: z.enum(NotificationContentModality),
    localizations: NotificationContentUpsertLocalizationMapSchema,
    intents: z.array(NotificationContentUpsertIntentSchema).default([]),
});
/**
 * A patch. At least one field must be present, and `deletedTimestamp` accepts only null — the
 * route un-deletes a content but never back-dates a deletion.
 */
export const NotificationContentUpdateSchema = z
    .object({
    name: z.string().optional(),
    organizationId: z.uuid().optional(),
    kind: z.enum(NotificationContentModality).optional(),
    localizations: NotificationContentUpsertLocalizationMapSchema.optional(),
    deletedTimestamp: z.null().optional(),
    intents: z.array(NotificationContentUpsertIntentSchema).optional(),
})
    .refine((values) => Object.keys(values).length > 0, {
    message: 'At least one field must be provided',
});
export const NOTIFICATION_CONTENT_LIST_DEFAULT_LIMIT = 50;
export const NOTIFICATION_CONTENT_LIST_MAX_LIMIT = 10_000;
/**
 * The filters, page size and cursor the list route accepts.
 *
 * Every parameter is a flat scalar or a repeated scalar, because that is all a query string
 * carries unambiguously: an id filter accepts one value or many rather than requiring bracket
 * syntax for a single one.
 */
export const NotificationContentListQuerySchema = z
    .object({
    excludedIds: OneOrManySchema(z.uuid()).optional().describe('Leave out these content ids.'),
    q: z.string().optional().describe('Return only contents whose name contains this text.'),
    kind: z
        .enum(NotificationContentModality)
        .optional()
        .describe('Return only contents written for this channel.'),
    includeDeleted: QueryBooleanSchema.optional().describe('Include contents that have been deleted.'),
    organizationIds: OneOrManySchema(z.uuid())
        .optional()
        .describe('Return only contents owned by these organizations.'),
    name: z
        .string()
        .optional()
        .describe('Return only contents whose name matches this value (case-insensitive). % and _ act as wildcards.'),
    source: z
        .enum(NotificationContentSource)
        .default(NotificationContentSource.Manual)
        .describe('Return only contents from this source. Defaults to the manually authored ones, so pass GENERATED or BROADCAST to see the rest.'),
    limit: z.coerce
        .number()
        .int()
        .positive()
        .max(NOTIFICATION_CONTENT_LIST_MAX_LIMIT)
        .optional()
        .default(NOTIFICATION_CONTENT_LIST_DEFAULT_LIMIT)
        .describe(`Contents per page. Defaults to ${NOTIFICATION_CONTENT_LIST_DEFAULT_LIMIT.toString()}, up to ${NOTIFICATION_CONTENT_LIST_MAX_LIMIT.toString()}.`),
    cursor: z
        .string()
        .min(1)
        .optional()
        .describe('Opaque cursor returned as meta.page.after by the previous page.'),
})
    .strict();
/** One page of contents ordered by name, in the envelope every cursor-paginated route returns. */
export const NotificationContentListResponseSchema = CursorPaginatedResponseSchema(NotificationContentSchema);
export const NotificationContentCloneSchema = z.object({
    newName: z.string(),
});
//# sourceMappingURL=content.js.map